import React, { useState, useEffect } from "react";
import Joyride, { ACTIONS, EVENTS, STATUS } from "react-joyride";
import { useSelector } from "react-redux";

// Import existing React Icons used in the project
import { FaHome, FaClock, FaSpinner, FaUndo, FaKey } from "react-icons/fa";
import { FaTrash } from "react-icons/fa6";
import {
  MdWaterDrop,
  MdCreditScore,
  MdPayment,
  MdOutlineTipsAndUpdates,
  MdSecurity,
} from "react-icons/md";
import { LiaFileInvoiceSolid } from "react-icons/lia";
import { RxDashboard } from "react-icons/rx";
import { IoPersonOutline } from "react-icons/io5";

const TenantTour = ({
  runTour,
  onTourEnd,
  sharedValue,
  setSharedValue,
  resumeAtStep,
}) => {
  const { user } = useSelector((state) => state.landlordslice);
  const [tourState, setTourState] = useState({
    run: false,
    stepIndex: 0,
    steps: [],
  });

  const [skipPasswordResetStep, setSkipPasswordResetStep] = useState(false);

  useEffect(() => {
    if (skipPasswordResetStep) {
      handleJoyrideCallback({
        action: ACTIONS.NEXT,
        index: 0,
        type: EVENTS.STEP_AFTER,
      });
    }
  }, [skipPasswordResetStep]);
  // Define tour steps for PC and large screens
  const tourSteps = [
    {
      target: "body",
      content: (
        <div>
          <h3
            style={{
              color: "#192A3A",
              marginBottom: "12px",
              fontFamily: "Karla, sans-serif",
              fontWeight: "600",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "8px",
              textAlign: "center",
            }}
          >
            <MdSecurity style={{ fontSize: "20px", color: "#B39359" }} />
            Welcome! Let's Secure Your Account
          </h3>
          <p style={{ fontFamily: "Poppins, sans-serif", fontSize: "14px" }}>
            Welcome to Rently Buzz! For your security, we highly recommend
            changing your password to something more secure and personal.
          </p>
          <div
            style={{
              marginTop: "12px",
              padding: "10px",
              backgroundColor: "#f8f6f3",
              borderRadius: "6px",
              border: "1px solid #B39359",
            }}
          >
            <strong
              style={{
                fontFamily: "Karla, sans-serif",
                color: "#192A3A",
                display: "flex",
                alignItems: "center",
                gap: "6px",
                marginBottom: "8px",
              }}
            >
              <FaKey style={{ fontSize: "16px", color: "#B39359" }} />
              Password Security Tips:
            </strong>
            <ul
              style={{
                marginTop: "8px",
                paddingLeft: "20px",
                fontFamily: "Poppins, sans-serif",
                fontSize: "13px",
              }}
            >
              <li>Use at least 8 characters</li>
              <li>Include uppercase and lowercase letters</li>
              <li>Add numbers and special characters</li>
              <li>Avoid personal information</li>
            </ul>
          </div>
          <p
            style={{
              marginTop: "10px",
              fontSize: "13px",
              color: "#6b7280",
              fontFamily: "Poppins, sans-serif",
              fontStyle: "italic",
              textAlign: "center",
            }}
          >
            Click "Next" to go to your profile page and change your password.
          </p>
          <button
            style={{
              marginTop: "16px",
              backgroundColor: "#B39359",
              color: "#fff",
              border: "none",
              borderRadius: "8px",
              padding: "10px 18px",
              fontFamily: "Karla, sans-serif",
              fontWeight: "600",
              fontSize: "14px",
              cursor: "pointer",
              boxShadow: "0 2px 8px rgba(25,42,58,0.08)",
              transition: "background 0.2s",
            }}
            onClick={() => {
              setSkipPasswordResetStep(true);
              // Trigger the next action to move to step 2 (skipping password step)
            }}
          >
            Skip Password Change Step
          </button>
        </div>
      ),
      placement: "center",
      disableBeacon: true,
      styles: {
        options: {
          primaryColor: "#192A3A",
        },
      },
    },
    {
      target: ".change-password-button",
      content: (
        <div>
          <h3
            style={{
              color: "#192A3A",
              marginBottom: "12px",
              fontFamily: "Karla, sans-serif",
              fontWeight: "600",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "8px",
              textAlign: "center",
            }}
          >
            <FaKey style={{ fontSize: "20px", color: "#B39359" }} />
            Change Your Password
          </h3>
          <p style={{ fontFamily: "Poppins, sans-serif", fontSize: "14px" }}>
            Here's your "Change Password" button! Click this button to update
            your password to something more secure and personal.
          </p>
          <div
            style={{
              marginTop: "12px",
              padding: "10px",
              backgroundColor: "#f8f6f3",
              borderRadius: "6px",
              border: "1px solid #B39359",
            }}
          >
            <strong
              style={{
                fontFamily: "Karla, sans-serif",
                color: "#192A3A",
                display: "flex",
                alignItems: "center",
                gap: "6px",
                marginBottom: "8px",
              }}
            >
              <MdSecurity style={{ fontSize: "16px", color: "#B39359" }} />
              Why Change Your Password?
            </strong>
            <ul
              style={{
                marginTop: "8px",
                paddingLeft: "20px",
                fontFamily: "Poppins, sans-serif",
                fontSize: "13px",
              }}
            >
              <li>Protects your personal and financial information</li>
              <li>Prevents unauthorized access to your account</li>
              <li>Ensures your rental payments and data stay secure</li>
            </ul>
          </div>
          <p
            style={{
              marginTop: "10px",
              fontSize: "13px",
              color: "#6b7280",
              fontFamily: "Poppins, sans-serif",
              fontStyle: "italic",
              textAlign: "center",
            }}
          >
            You can change your password now or continue the tour and do it
            later.
          </p>
        </div>
      ),
      placement: "top",
      styles: {
        options: {
          primaryColor: "#192A3A",
        },
      },
    },
    {
      target: ".cards-container",
      content: (
        <div>
          <h3
            style={{
              color: "#192A3A",
              marginBottom: "12px",
              fontFamily: "Karla, sans-serif",
              fontWeight: "600",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "8px",
              textAlign: "center",
            }}
          >
            <RxDashboard style={{ fontSize: "20px", color: "#B39359" }} />
            Payment Reports Dashboard
          </h3>
          <p style={{ fontFamily: "Poppins, sans-serif", fontSize: "14px" }}>
            Welcome to your payment dashboard! These information cards provide a
            comprehensive overview of your payment progress and financial
            status.
          </p>
          <ul
            style={{
              marginTop: "10px",
              paddingLeft: "20px",
              fontFamily: "Poppins, sans-serif",
              fontSize: "14px",
            }}
          >
            <li>
              <strong>Invoices Pending:</strong> Shows outstanding payments you
              need to make
            </li>
            <li>
              <strong>Invoices Paid:</strong> Displays your completed payments
              and history
            </li>
            <li>
              <strong>Arrears:</strong> Tracks any overdue amounts that need
              attention
            </li>
          </ul>
          <p
            style={{
              marginTop: "10px",
              fontSize: "13px",
              color: "#6b7280",
              fontFamily: "Poppins, sans-serif",
              fontStyle: "italic",
              display: "flex",
              alignItems: "center",
              gap: "4px",
            }}
          >
            <MdOutlineTipsAndUpdates
              style={{ fontSize: "24px", color: "#B39359" }}
            />
            Tip: These cards update in real-time as you make payments!
          </p>
        </div>
      ),
      placement: "bottom",
      disableBeacon: true,
      styles: {
        options: {
          primaryColor: "#192A3A",
        },
      },
    },
    {
      target: ".quick-links-card-container",
      content: (
        <div>
          <h3
            style={{
              color: "#B39359",
              marginBottom: "12px",
              fontFamily: "Karla, sans-serif",
              fontWeight: "600",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "8px",
              textAlign: "center",
            }}
          >
            <MdPayment style={{ fontSize: "20px", color: "#192A3A" }} />
            Quick Payment Links
          </h3>
          <p style={{ fontFamily: "Poppins, sans-serif", fontSize: "14px" }}>
            Need to make a payment quickly? These quick links allow you to pay
            specific bills directly from the overview page without navigating to
            different sections.
          </p>
          <div
            style={{
              marginTop: "12px",
              padding: "10px",
              backgroundColor: "#f8f6f3",
              borderRadius: "6px",
              border: "1px solid #B39359",
            }}
          >
            <strong
              style={{
                fontFamily: "Karla, sans-serif",
                color: "#192A3A",
                display: "block",
                textAlign: "center",
                marginBottom: "8px",
              }}
            >
              Available quick payments:
            </strong>
            <ul
              style={{
                marginTop: "8px",
                paddingLeft: "20px",
                fontFamily: "Poppins, sans-serif",
                fontSize: "13px",
              }}
            >
              <li
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  marginBottom: "4px",
                }}
              >
                <FaHome style={{ color: "#B39359", fontSize: "14px" }} />
                <strong>Rent</strong> - Monthly rent payments
              </li>
              <li
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  marginBottom: "4px",
                }}
              >
                <MdWaterDrop style={{ color: "#B39359", fontSize: "14px" }} />
                <strong>Water</strong> - Water bill payments
              </li>
              <li
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  marginBottom: "4px",
                }}
              >
                <FaClock style={{ color: "#B39359", fontSize: "14px" }} />
                <strong>Arrears</strong> - Outstanding balance payments
              </li>
              <li
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  marginBottom: "4px",
                }}
              >
                <LiaFileInvoiceSolid
                  style={{ color: "#B39359", fontSize: "14px" }}
                />
                <strong>Service Charge</strong> - Property service fees
              </li>
              <li
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  marginBottom: "4px",
                }}
              >
                <FaSpinner style={{ color: "#B39359", fontSize: "14px" }} />
                <strong>Unpaid Deposit</strong> - Security deposit payments
              </li>
              <li
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  marginBottom: "4px",
                }}
              >
                <FaTrash style={{ color: "#B39359", fontSize: "14px" }} />
                <strong>Garbage</strong> - Waste management fees
              </li>
            </ul>
          </div>
          <p
            style={{
              marginTop: "10px",
              fontSize: "13px",
              color: "#6b7280",
              fontFamily: "Poppins, sans-serif",
              fontStyle: "italic",
              display: "flex",
              alignItems: "center",
              gap: "4px",
            }}
          >
            <MdOutlineTipsAndUpdates
              style={{ fontSize: "24px", color: "#B39359" }}
            />
            Click any card to open a payment modal with pre-filled amounts!
          </p>
        </div>
      ),
      placement: "top",
      styles: {
        options: {
          primaryColor: "#B39359",
        },
      },
    },
    {
      target: ".mini-card-container",
      content: (
        <div>
          <h3
            style={{
              color: "#192A3A",
              marginBottom: "12px",
              fontFamily: "Karla, sans-serif",
              fontWeight: "600",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "8px",
              textAlign: "center",
            }}
          >
            <MdCreditScore style={{ fontSize: "20px", color: "#B39359" }} />
            Payment Progress Visualization
          </h3>
          <p style={{ fontFamily: "Poppins, sans-serif", fontSize: "14px" }}>
            These mini cards provide a visual representation of your payment
            progress in each category. Each card shows:
          </p>
          <div
            style={{
              marginTop: "12px",
              padding: "10px",
              backgroundColor: "#f4f2ef",
              borderRadius: "6px",
              border: "1px solid #192A3A",
            }}
          >
            <ul
              style={{
                paddingLeft: "20px",
                fontFamily: "Poppins, sans-serif",
                fontSize: "13px",
              }}
            >
              <li>
                <strong>Amount Paid:</strong> How much you've already paid
              </li>
              <li>
                <strong>Progress Percentage:</strong> Visual progress bar
                showing completion
              </li>
              <li>
                <strong>Category Icons:</strong> Easy identification of payment
                types
              </li>
            </ul>
          </div>
          {/* <div
            style={{
              marginTop: "12px",
              padding: "10px",
              backgroundColor: "#f8f6f3",
              borderRadius: "6px",
              border: "1px solid #B39359",
            }}
          >
            <strong
              style={{
                fontFamily: "Karla, sans-serif",
                color: "#192A3A",
                display: "flex",
                alignItems: "center",
                gap: "6px",
              }}
            >
              <RxDashboard style={{ fontSize: "16px", color: "#B39359" }} />
              What the colors mean:
            </strong>
            <ul
              style={{
                marginTop: "8px",
                paddingLeft: "20px",
                fontFamily: "Poppins, sans-serif",
                fontSize: "13px",
              }}
            >
              <li>
                🟢 <strong>Green:</strong> Good progress or completed payments
              </li>
              <li>
                🟡 <strong>Yellow:</strong> Partial payments made
              </li>
              <li>
                🔴 <strong>Red:</strong> Urgent attention needed
              </li>
            </ul>
          </div> */}
          <p
            style={{
              marginTop: "10px",
              fontSize: "13px",
              color: "#6b7280",
              fontFamily: "Poppins, sans-serif",
              fontStyle: "italic",
              display: "flex",
              alignItems: "center",
              gap: "4px",
            }}
          >
            <MdOutlineTipsAndUpdates
              style={{ fontSize: "24px", color: "#B39359" }}
            />
            Track your progress at a glance and stay on top of your payments!
          </p>
        </div>
      ),
      placement: "left",
      styles: {
        options: {
          primaryColor: "#192A3A",
        },
      },
    },
    {
      target: ".make-payment-button",
      content: (
        <div>
          <h3
            style={{
              color: "#B39359",
              marginBottom: "12px",
              fontFamily: "Karla, sans-serif",
              fontWeight: "600",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "8px",
              textAlign: "center",
            }}
          >
            <MdPayment style={{ fontSize: "20px", color: "#192A3A" }} />
            Make Payment Button
          </h3>
          <p style={{ fontFamily: "Poppins, sans-serif", fontSize: "14px" }}>
            Your main payment gateway! Click this button to open a comprehensive
            payment modal where you can:
          </p>
          <ul
            style={{
              marginTop: "10px",
              paddingLeft: "20px",
              fontFamily: "Poppins, sans-serif",
              fontSize: "13px",
            }}
          >
            <li>Pay multiple bills at once</li>
            <li>Choose your preferred payment method</li>
            <li>View and confirm payment details</li>
            <li>Track payment history</li>
          </ul>
          <p
            style={{
              marginTop: "12px",
              fontSize: "13px",
              color: "#6b7280",
              fontFamily: "Poppins, sans-serif",
              fontStyle: "italic",
              display: "flex",
              alignItems: "center",
              gap: "4px",
            }}
          >
            <MdOutlineTipsAndUpdates
              style={{ fontSize: "16px", color: "#B39359" }}
            />
            This is your one-stop solution for all payment needs!
          </p>
        </div>
      ),
      placement: "bottom",
      styles: {
        options: {
          primaryColor: "#B39359",
        },
      },
    },
    {
      target: ".transactions-container",
      content: (
        <div>
          <h3
            style={{
              color: "#192A3A",
              marginBottom: "12px",
              fontFamily: "Karla, sans-serif",
              fontWeight: "600",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "8px",
              textAlign: "center",
            }}
          >
            <LiaFileInvoiceSolid
              style={{ fontSize: "20px", color: "#B39359" }}
            />
            Transaction History
          </h3>
          <p style={{ fontFamily: "Poppins, sans-serif", fontSize: "14px" }}>
            Keep track of all your payment activities with this comprehensive
            transaction table. Here you can:
          </p>
          <ul
            style={{
              marginTop: "10px",
              paddingLeft: "20px",
              fontFamily: "Poppins, sans-serif",
              fontSize: "13px",
            }}
          >
            <li>View recent payment history</li>
            <li>Check payment dates and amounts</li>
            <li>Monitor payment status</li>
            <li>Download payment receipts</li>
          </ul>
          <div
            style={{
              marginTop: "12px",
              padding: "10px",
              backgroundColor: "#f8f6f3",
              borderRadius: "6px",
              border: "1px solid #B39359",
            }}
          >
            <strong
              style={{
                fontFamily: "Karla, sans-serif",
                color: "#192A3A",
                display: "flex",
                alignItems: "center",
                gap: "6px",
              }}
            >
              <MdOutlineTipsAndUpdates
                style={{ fontSize: "24px", color: "#B39359" }}
              />
              Pro tip:
            </strong>
            <span
              style={{ fontFamily: "Poppins, sans-serif", fontSize: "13px" }}
            >
              {" "}
              Use this section to verify payments and keep records for your
              personal finance tracking.
            </span>
          </div>
        </div>
      ),
      placement: "top",
      styles: {
        options: {
          primaryColor: "#192A3A",
        },
      },
    },
    {
      target: ".first-card-section",
      content: (
        <div>
          <h3
            style={{
              color: "#B39359",
              marginBottom: "12px",
              fontFamily: "Karla, sans-serif",
              fontWeight: "600",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "8px",
              textAlign: "center",
            }}
          >
            <FaHome style={{ fontSize: "20px", color: "#192A3A" }} />
            Property & Contact Information
          </h3>
          <p style={{ fontFamily: "Poppins, sans-serif", fontSize: "14px" }}>
            This sidebar contains important information about your rental
            property and emergency contacts:
          </p>
          <ul
            style={{
              marginTop: "10px",
              paddingLeft: "20px",
              fontFamily: "Poppins, sans-serif",
              fontSize: "13px",
            }}
          >
            <li>
              <strong>Room Details:</strong> Your apartment information and
              lease details
            </li>
            <li>
              <strong>Caretaker Contact:</strong> Reach out for maintenance or
              emergencies
            </li>
            <li>
              <strong>Property Manager:</strong> Contact for lease and payment
              inquiries
            </li>
          </ul>
          <div
            style={{
              marginTop: "12px",
              padding: "10px",
              backgroundColor: "#f8f6f3",
              borderRadius: "6px",
              border: "1px solid #B39359",
            }}
          >
            <strong
              style={{
                fontFamily: "Karla, sans-serif",
                color: "#192A3A",
                display: "flex",
                alignItems: "center",
                gap: "6px",
              }}
            >
              <IoPersonOutline style={{ fontSize: "16px", color: "#B39359" }} />
              Emergency Contacts:
            </strong>
            <span
              style={{ fontFamily: "Poppins, sans-serif", fontSize: "13px" }}
            >
              {" "}
              Keep these numbers handy for urgent situations!
            </span>
          </div>
        </div>
      ),
      placement: "left",
      styles: {
        options: {
          primaryColor: "#B39359",
        },
      },
    },
  ];

  useEffect(() => {
    if (runTour) {
      // Check if user is on PC/large screen
      const isLargeScreen = window.innerWidth >= 1024;

      if (isLargeScreen) {
        // Delay tour start to ensure DOM elements are ready
        const timer = setTimeout(() => {
          setTourState({
            run: true,
            stepIndex: resumeAtStep !== null ? resumeAtStep : 0,
            steps: tourSteps,
          });
        }, 1000); // 1 second delay to ensure components are mounted

        return () => clearTimeout(timer);
      }
    }
  }, [runTour, resumeAtStep]);

  const handleJoyrideCallback = (data) => {
    const { action, index, status, type } = data;

    if (
      [STATUS.FINISHED, STATUS.SKIPPED].includes(status) ||
      action === ACTIONS.CLOSE
    ) {
      setTourState({ ...tourState, run: false });

      // Mark tour as completed for this user
      localStorage.setItem(
        `tenant_tour_completed_${user?.data?.user?.user_id}`,
        "true"
      );

      if (onTourEnd) {
        onTourEnd();
      }
    } else if ([EVENTS.STEP_AFTER, EVENTS.TARGET_NOT_FOUND].includes(type)) {
      const nextStepIndex = index + (action === ACTIONS.PREV ? -1 : 1);

      // Special handling for switching between Profile and Dashboard views
      if (index === 0 && action === ACTIONS.NEXT) {
        if (skipPasswordResetStep) {
          setTimeout(() => {
            setTourState({ ...tourState, stepIndex: 2 });
          }, 500);
        } else {
          // Switch to profile view when moving from welcome step to password step
          setSharedValue("Profile");

          // Delay the step progression to allow view switch
          setTimeout(() => {
            setTourState({ ...tourState, stepIndex: nextStepIndex });
          }, 500);
        }
      } else if (index === 1 && action === ACTIONS.NEXT) {
        // Switch back to dashboard when moving from password step to dashboard tour
        setSharedValue("Dashboard");

        // Delay the step progression to allow view switch
        setTimeout(() => {
          setTourState({ ...tourState, stepIndex: nextStepIndex });
        }, 500);
      } else if (index === 2 && action === ACTIONS.PREV) {
        // Switch back to profile when going back from dashboard to password step
        setSharedValue("Profile");

        // Delay the step progression to allow view switch
        setTimeout(() => {
          setTourState({ ...tourState, stepIndex: nextStepIndex });
        }, 500);
      } else if (index === 1 && action === ACTIONS.PREV) {
        // Switch back to dashboard when going back from password step to welcome step
        setSharedValue("Dashboard");

        // Delay the step progression to allow view switch
        setTimeout(() => {
          setTourState({ ...tourState, stepIndex: nextStepIndex });
        }, 500);
      } else {
        setTourState({ ...tourState, stepIndex: nextStepIndex });
      }
    }
  };

  // Custom styles for the tour
  const tourStyles = {
    options: {
      arrowColor: "#fff",
      backgroundColor: "#fff",
      overlayColor: "rgba(25, 42, 58, 0.5)",
      primaryColor: "#192A3A",
      textColor: "#192A3A",
      width: 420,
      zIndex: 1000,
    },
    tooltip: {
      borderRadius: "12px",
      fontSize: "16px",
      padding: "20px",
      boxShadow:
        "0 20px 25px -5px rgba(25, 42, 58, 0.2), 0 10px 10px -5px rgba(25, 42, 58, 0.1)",
      fontFamily: "Poppins, sans-serif",
    },
    tooltipContent: {
      padding: "0",
      fontFamily: "Poppins, sans-serif",
    },
    buttonNext: {
      backgroundColor: "#192A3A",
      borderRadius: "8px",
      fontSize: "14px",
      fontFamily: "Karla, sans-serif",
      fontWeight: "600",
      padding: "10px 20px",
      transition: "all 0.2s ease",
    },
    buttonBack: {
      color: "#6b7280",
      fontSize: "14px",
      fontWeight: "600",
      marginRight: "10px",
      fontFamily: "Karla, sans-serif",
    },
    buttonSkip: {
      color: "#B39359",
      fontSize: "14px",
      fontWeight: "600",
      fontFamily: "Karla, sans-serif",
    },
    buttonClose: {
      display: "none",
    },
    beacon: {
      inner: "#192A3A",
      outer: "#B39359",
    },
    spotlight: {
      borderRadius: "8px",
    },
  };

  return (
    <Joyride
      steps={tourState.steps}
      run={tourState.run}
      stepIndex={tourState.stepIndex}
      callback={handleJoyrideCallback}
      continuous={true}
      showProgress={true}
      showSkipButton={true}
      disableOverlayClose={true}
      disableCloseOnEsc={false}
      hideCloseButton={false}
      scrollToFirstStep={true}
      scrollOffset={100}
      styles={tourStyles}
      locale={{
        back: "← Back",
        close: "Close",
        last: "Finish Tour",
        next: "Next →",
        skip: "Skip Tour",
      }}
    />
  );
};

export default TenantTour;
